// Dependencies
import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import moment from 'moment';

// Components
import Layout from '../../components/layout/layout';
import Seo from '../../components/seo/seo';
import BlogPostHeader from '../../components/blog/blogPostHeader/blogPostHeader';
import BlogPostExcerpt from '../../components/blog/blogPostExcerpt/blogPostExcerpt';
import BlogPostSidebar from '../../components/blog/blogPostSidebar/blogPostSidebar';
import Container from '../../components/container/container';
import BlogPostRelatedPosts from '../../components/blog/blogPostRelatedPosts/blogPostRelatedPosts';
import BlogPostAuthorInfo from '../../components/blog/blogPostAuthorInfo/blogPostAuthorInfo';
import CallToActionBanner from '../../components/flexibleContent/callToActionBanner/callToActionBanner';
import BlogPostShare from '../../components/blog/blogPostShare/blogPostShare';
import BlogPostNewsletterForm from '../../components/blog/blogPostNewsletterForm/blogPostNewsletterForm';
import BlogPostFlexibleContentFactory from '../../components/flexibleContent/blog/blogPostFlexibleContentFactory/blogPostFlexibleContentFactory';

// Hooks
import useBlogPostDefaultCtaOptions from '../../hooks/useBlogPostDefaultCtaOptions';
import useBrandingOptions from '../../hooks/useBrandingOptions';

// Utilities
import calculateWordCount from '../../utilities/calculateWordCount';

export default function BlogPost({
  data: { post, defaultThumbnail, wp },
  location,
}) {
  const {
    title,
    content,
    featuredImage,
    relatedPosts,
    categories,
    uri,
    blogFlexibleContent,
  } = post;
  const author = post?.author?.node;

  const yoast = post?.seo;

  const excerpt = post?.seo?.metaDesc ? `<p>${post?.seo?.metaDesc}</p>` : '';

  const ctaDefaultOptions = useBlogPostDefaultCtaOptions();
  const blogPostBannerOptions = post?.blogPostBannerCTA;
  const overrideBanner = blogPostBannerOptions?.override;

  const SITE_URL = process.env.GATSBY_SITE_URL;
  const brandingOptions = useBrandingOptions();
  const logoImg = brandingOptions?.logo?.localFile?.publicURL;
  const wordCount = calculateWordCount(content);

  const schemaMarkup = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}${post?.uri}`,
      },
      headline: title,
      description: yoast?.metaDesc || wp?.generalSettings?.description,
      image: yoast?.opengraphImage?.publicUrl
        ? `${SITE_URL}${yoast?.opengraphImage?.publicUrl}`
        : `${SITE_URL}${defaultThumbnail?.publicURL}`,
      author: {
        '@type': 'Person',
        name: `${author?.firstName} ${author?.lastName}`,
        url: `${SITE_URL}${author?.uri}`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Maclynn',
        '@id': SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: logoImg,
        },
      },
      datePublished: moment(new Date(post?.date)).format('YYYY-MM-DD'),
      dateModified: moment(
        new Date(post?.modified) || new Date(post?.date),
      ).format('YYYY-MM-DD'),
      wordcount: wordCount,
      isPartOf: {
        '@id': `${SITE_URL}/blog`,
        name: 'Maclynn Blog',
        publisher: {
          '@type': 'Organization',
          name: 'Maclynn',
          '@id': SITE_URL,
        },
      },
    },
  ];

  const featuredImageUrl = featuredImage?.node?.localFile?.publicURL;

  return (
    <Layout>
      <Seo
        yoast={yoast}
        author={`${author?.firstName} ${author?.lastName}`}
        schemaMarkup={schemaMarkup}
        socialImageUrl={featuredImageUrl}
        locationData={location}
      />

      <BlogPostHeader
        title={title}
        content={content}
        readingTime={blogFlexibleContent?.readingTime}
        categories={categories}
        uri={uri}
      />

      <BlogPostExcerpt excerpt={excerpt} featuredImage={featuredImage} />

      <Container flex>
        <BlogPostSidebar author={author} />

        <BlogPostFlexibleContentFactory
          layouts={blogFlexibleContent?.layouts}
        />

        <BlogPostNewsletterForm />
        <BlogPostShare
          title={title}
          featuredImage={featuredImage}
          datePublished={post?.date}
          dateModified={post?.modified}
        />
      </Container>

      <BlogPostAuthorInfo author={author} />

      <BlogPostRelatedPosts relatedPosts={relatedPosts?.nodes} />

      <div className="spacing-standard">
        <CallToActionBanner
          layout={{
            heading: overrideBanner
              ? blogPostBannerOptions?.heading
              : ctaDefaultOptions?.bannerHeading,
            text: overrideBanner
              ? blogPostBannerOptions?.text
              : ctaDefaultOptions?.bannerText,
            backgroundImage: overrideBanner
              ? blogPostBannerOptions?.backgroundImage
              : ctaDefaultOptions?.bannerBackgroundImage,
            buttons: overrideBanner
              ? blogPostBannerOptions?.buttons
              : ctaDefaultOptions?.bannerButtons,
            spacing: 'standard',
          }}
        />
      </div>
    </Layout>
  );
}

export const pageQuery = graphql`
  query BlogPostById($id: String!) {
    post: wpPost(id: { eq: $id }) {
      ...blogPostFields
      seo {
        ...seoFields
      }
    }
    defaultThumbnail: file(relativePath: { eq: "meta/thumbnail.jpg" }) {
      publicURL
    }
    wp {
      generalSettings {
        title
        description
      }
    }
  }
`;

BlogPost.propTypes = {
  data: PropTypes.shape({
    post: PropTypes.shape({
      title: PropTypes.string,
      date: PropTypes.string,
      modified: PropTypes.string,
      content: PropTypes.string,
      excerpt: PropTypes.string,
      uri: PropTypes.string,
      blogFlexibleContent: PropTypes.shape({
        readingTime: PropTypes.string,
        layouts: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      featuredImage: PropTypes.shape({
        node: PropTypes.shape({
          altText: PropTypes.string,
          gatsbyImage: PropTypes.shape({}),
          localFile: PropTypes.shape({ publicURL: PropTypes.string }),
        }),
      }),
      author: PropTypes.shape({
        firstName: PropTypes.string,
        node: PropTypes.shape({
          blogProfile: PropTypes.shape({
            jobTitle: PropTypes.string,
            profilePhoto: PropTypes.shape({
              altText: PropTypes.string,
              gatsbyImage: PropTypes.shape({}),
            }),
          }),
        }),
      }),
      categories: PropTypes.shape({
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
          }),
        ),
      }),
      blogPost: PropTypes.shape({}),
      blogPostBannerCTA: PropTypes.shape({}),
      blogPostContentCTA: PropTypes.shape({}),
      relatedPosts: PropTypes.shape({
        nodes: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      seo: PropTypes.shape({
        metaDesc: PropTypes.string,
      }),
    }),
    defaultThumbnail: PropTypes.shape({
      publicURL: PropTypes.string,
    }),
    wp: PropTypes.shape({
      generalSettings: PropTypes.shape({
        description: PropTypes.string,
      }),
    }),
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
};
